/**
 * <yugal-reveal>: the reveal page. Reads ?r=<token>, shows a "tap to open" curtain, and on tap
 * reveals the portrait, the gift note, the audio note and the tradition's story while its music
 * fades in. The tap matters: browsers only allow sound after a user gesture.
 */
(() => {
  const FADE_MS = 2500;
  const MUSIC_VOLUME = 0.35;

  class YugalReveal extends HTMLElement {
    connectedCallback() {
      this.api = (this.dataset.api || '').replace(/\/+$/, '');
      this.strings = JSON.parse(this.querySelector('[data-strings]')?.textContent || '{}');
      this.token = new URLSearchParams(window.location.search).get('r') || '';
      this.load();
    }

    $(selector) {
      return this.querySelector(selector);
    }

    t(key) {
      return this.strings[key] ?? key;
    }

    async load() {
      const headline = this.$('[data-headline]');
      const subline = this.$('[data-subline]');
      try {
        if (!this.token || !this.api) throw Object.assign(new Error('not found'), { status: 404 });
        const response = await fetch(`${this.api}/reveal/${encodeURIComponent(this.token)}`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw Object.assign(new Error(data.error || 'error'), { status: response.status });
        this.data = data;

        if (data.state !== 'open') {
          headline.textContent = this.t('on_its_way');
          subline.textContent = this.t('on_its_way_text');
          subline.hidden = false;
          return;
        }
        headline.textContent = data.is_gift ? this.t('curtain_gift') : this.t('curtain_self');
        this.prepare(data);
        const open = this.$('[data-open]');
        open.hidden = false;
        open.addEventListener('click', () => this.open(), { once: true });
      } catch (err) {
        headline.textContent = err.status === 404 ? this.t('not_found') : this.t('error');
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    prepare(data) {
      const host = this.$('[data-portraits]');
      for (const p of data.portraits) {
        if (!p.image_url) continue;
        const figure = document.createElement('figure');
        figure.className = 'yr__portrait';
        const img = document.createElement('img');
        img.src = p.image_url;
        img.alt = p.style?.name ? `${this.t('tradition')} ${p.style.name}` : '';
        img.width = 800;
        img.height = 1200;
        // Hung as a canvas on a wall (snippets/yugal-canvas.liquid tree, styled in yugal.css).
        const canvas = document.createElement('div');
        canvas.className = 'y-canvas y-canvas--portrait y-canvas--lg';
        const wall = document.createElement('div');
        wall.className = 'y-canvas__wall';
        const art = document.createElement('div');
        art.className = 'y-canvas__art';
        art.append(img);
        wall.append(art);
        canvas.append(wall);
        figure.append(canvas);
        host.append(figure);
      }

      if (data.gift_note) {
        const note = this.$('[data-note]');
        note.textContent = data.gift_note;
        note.hidden = false;
      }

      if (data.audio_url) {
        const audio = this.$('[data-note-audio]');
        const button = this.$('[data-play-note]');
        audio.src = data.audio_url;
        this.$('[data-audio-row]').hidden = false;
        button.addEventListener('click', () => {
          if (audio.paused) {
            this.fadeTo(this.$('[data-music]'), MUSIC_VOLUME * 0.3);
            audio.play().catch(() => {});
          } else {
            audio.pause();
          }
        });
        audio.addEventListener('play', () => (button.textContent = this.t('pause_note')));
        const reset = () => {
          button.textContent = this.t('play_note');
          this.fadeTo(this.$('[data-music]'), MUSIC_VOLUME);
        };
        audio.addEventListener('pause', reset);
        audio.addEventListener('ended', reset);
      }

      const story = this.$('[data-story]');
      const first = data.portraits.find((p) => p.style);
      if (first) {
        const label = document.createElement('p');
        label.className = 'y-eyebrow';
        label.textContent = this.t('tradition');
        const name = document.createElement('p');
        name.className = 'yr__story-name';
        name.textContent = first.style.name;
        const meta = document.createElement('p');
        meta.className = 'y-meta';
        meta.textContent = [first.style.region, first.style.period].filter(Boolean).join(' · ');
        story.append(label, name, meta);
        if (first.style.note) {
          const note = document.createElement('p');
          note.className = 'y-body';
          note.textContent = first.style.note;
          story.append(note);
        }
      }

      const musicUrl = data.portraits.find((p) => p.music_url)?.music_url;
      if (musicUrl) {
        const music = this.$('[data-music]');
        music.src = musicUrl;
        const toggle = this.$('[data-music-toggle]');
        toggle.hidden = false;
        toggle.addEventListener('click', () => {
          if (music.paused) {
            music.play().catch(() => {});
            this.fadeTo(music, MUSIC_VOLUME);
            toggle.textContent = this.t('music_off');
          } else {
            music.pause();
            toggle.textContent = this.t('music_on');
          }
        });
      }
    }

    open() {
      this.$('[data-stage]').hidden = false;
      const music = this.$('[data-music]');
      if (music.src) {
        music.volume = 0;
        music.play().then(() => this.fadeTo(music, MUSIC_VOLUME)).catch(() => {
          this.$('[data-music-toggle]').textContent = this.t('music_on');
        });
      }
      // Next frame, so the stage is laid out before the transitions start.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          this.toggleAttribute('data-opened', true);
          this.$('[data-portraits]').scrollIntoView({ behavior: 'smooth', block: 'start' });
          window.setTimeout(() => (this.$('[data-curtain]').hidden = true), 1400);
        }),
      );
    }

    /** Volume fades are done by hand: iOS ignores volume, which simply means no fade there. */
    fadeTo(audio, target) {
      if (!audio?.src) return;
      cancelAnimationFrame(this.fade);
      const start = audio.volume;
      const began = performance.now();
      const step = (now) => {
        const k = Math.min(1, (now - began) / FADE_MS);
        audio.volume = start + (target - start) * k;
        if (k < 1) this.fade = requestAnimationFrame(step);
      };
      this.fade = requestAnimationFrame(step);
    }
  }

  if (!customElements.get('yugal-reveal')) customElements.define('yugal-reveal', YugalReveal);
})();
